import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { getSupabase } from '../lib/supabase';
import { clearPendingPasswordSetup, markPendingPasswordSetup } from '../lib/authFlow';
import type { AuthUser } from '../types/auth';

// Auth layer only: the Supabase session. Application identity (users.id, company, roles)
// is resolved from the backend by IdentityProvider — roles are NOT read from the JWT.
interface AuthState {
  readonly status: 'loading' | 'authenticated' | 'anonymous';
  readonly authUser: AuthUser | null;
  readonly signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

function userFromSession(session: Session | null): AuthUser | null {
  if (session === null) {
    return null;
  }
  return { authUserId: session.user.id, email: session.user.email ?? null };
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const supabase = getSupabase();
    let active = true;

    void supabase.auth.getSession().then(({ data }) => {
      if (active) {
        setSession(data.session);
        setLoading(false);
      }
    });

    // The event name is NOT ignored (Slice P0-E). Measured against the real SDK: a recovery
    // link emits PASSWORD_RECOVERY, an invite link emits SIGNED_IN — so this covers recovery
    // (including a link opened while the app is already running, where no page load happens to
    // read the URL), and authFlow's URL capture covers invite. SIGNED_OUT clears the flag so a
    // pending setup can never outlive the session that created it.
    const { data: sub } = supabase.auth.onAuthStateChange((event, next) => {
      if (event === 'PASSWORD_RECOVERY') {
        markPendingPasswordSetup('recovery');
      } else if (event === 'SIGNED_OUT') {
        clearPendingPasswordSetup();
      }
      setSession(next);
      setLoading(false);
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthState>(() => {
    const authUser = userFromSession(session);
    return {
      status: loading ? 'loading' : authUser ? 'authenticated' : 'anonymous',
      authUser,
      signOut: async () => {
        await getSupabase().auth.signOut();
      },
    };
  }, [session, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
